import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Stack,
  Typography,
} from '@mui/material';
import { useNotification } from '@/contexts/NotificationContext';
import type { Trader } from '@/types/trader';

/** Лимиты OUT для PUT /traders/{id} перед включением пула. */
export interface TraderOutLimitsEnablePayload {
  max_active_payouts: number;
  daily_payout_count_limit: number | null;
  daily_payout_amount_limit: string | null;
}

interface TraderOutLimitsEnableDialogProps {
  open: boolean;
  trader: Trader | null;
  onClose: () => void;
  /** Сохранить лимиты (PUT /traders); родитель затем вызывает включение доступа OUT. */
  onConfirm: (payload: TraderOutLimitsEnablePayload) => Promise<void>;
}

const TraderOutLimitsEnableDialog: React.FC<TraderOutLimitsEnableDialogProps> = ({
  open,
  trader,
  onClose,
  onConfirm,
}) => {
  const { showNotification } = useNotification();
  const [maxActive, setMaxActive] = useState('');
  const [dailyCount, setDailyCount] = useState('');
  const [dailyAmount, setDailyAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !trader) {
      return;
    }
    setMaxActive(
      trader.max_active_payouts !== undefined && trader.max_active_payouts !== null
        ? String(trader.max_active_payouts)
        : '',
    );
    setDailyCount(
      trader.daily_payout_count_limit !== undefined && trader.daily_payout_count_limit !== null
        ? String(trader.daily_payout_count_limit)
        : '',
    );
    setDailyAmount(
      trader.daily_payout_amount_limit !== undefined && trader.daily_payout_amount_limit !== null
        ? String(trader.daily_payout_amount_limit)
        : '',
    );
    setSubmitting(false);
  }, [open, trader]);

  const handleConfirm = async () => {
    const maxStr = maxActive.trim();
    const maxParsed = Number(maxStr);
    if (maxStr === '' || !Number.isInteger(maxParsed) || maxParsed < 0) {
      showNotification('Укажите целое число ≥ 0 для макс. активных выплат', 'error');
      return;
    }

    const countStr = dailyCount.trim();
    let dailyCountLimit: number | null;
    if (countStr === '') {
      dailyCountLimit = null;
    } else {
      const c = Number(countStr);
      if (!Number.isInteger(c) || c < 0) {
        showNotification('Дневной лимит по количеству: целое число ≥ 0 или пусто', 'error');
        return;
      }
      dailyCountLimit = c;
    }

    const amountStr = dailyAmount.trim();
    const dailyAmountLimit: string | number | null = amountStr === '' ? null : amountStr;

    const payload: TraderOutLimitsEnablePayload = {
      max_active_payouts: maxParsed,
      daily_payout_count_limit: dailyCountLimit,
      daily_payout_amount_limit: dailyAmountLimit,
    };

    setSubmitting(true);
    try {
      await onConfirm(payload);
    } catch {
      /* ошибка уже обработана в родителе */
    } finally {
      setSubmitting(false);
    }
  };

  if (!trader) {
    return null;
  }

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Включение выплат OUT</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Задайте лимиты и подтвердите. Доступ к пулу OUT будет включён только после успешного сохранения.
          </Typography>
          <TextField
            label="Макс. активных выплат (ACCEPTED + IN_PROGRESS)"
            value={maxActive}
            onChange={(e) => setMaxActive(e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            required
            disabled={submitting}
          />
          <TextField
            label="Дневной лимит по количеству (пусто — без лимита)"
            value={dailyCount}
            onChange={(e) => setDailyCount(e.target.value)}
            type="number"
            inputProps={{ min: 0, step: 1 }}
            fullWidth
            disabled={submitting}
          />
          <TextField
            label="Дневной лимит по сумме (пусто — без лимита)"
            value={dailyAmount}
            onChange={(e) => setDailyAmount(e.target.value)}
            fullWidth
            disabled={submitting}
            helperText="Число или decimal-строка, как в API"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Отмена
        </Button>
        <Button variant="contained" onClick={handleConfirm} disabled={submitting}>
          Подтвердить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TraderOutLimitsEnableDialog;
